import { Chain } from '@ankr.com/chains-list';
import { useMemo } from 'react';

import { useSubchainID } from 'modules/chains/hooks/useSubchainID';

import { ChainSelectorsContext } from './context';
import { ChainSelectorsContextValue } from './types';
import { useChainCallProtocol } from './hooks/useChainCallProtocol';
import { useChainSubType } from './hooks/useChainSubType';
import { useChainType } from './hooks/useChainType';
import { useConsensus } from './hooks/useConsensus';
import { useGroups } from './hooks/useGroups';

interface StatisticsDataProviderProps {
  chain: Chain;
  children: React.ReactNode;
  hasConsensusSwitch?: boolean;
  shouldExpandMainnets?: boolean;
  shouldExpandTestnets?: boolean;
  shouldMergeTacTendermintGroups?: boolean;
  shouldMergeTendermintGroups?: boolean;
  shouldMergeTonGroups?: boolean;
  shouldSkipChainTypeReset?: boolean; // this flag added in order to skip chain type reset on chain switch in copy endpoint modal
  shouldMergeExtensions?: boolean; // this flag added in order to merge extensions for custom chains to show endpoints without selectors
}

export const ChainSelectorsProvider = ({
  chain,
  children,
  hasConsensusSwitch = true,
  shouldExpandMainnets,
  shouldExpandTestnets,
  shouldMergeExtensions,
  shouldMergeTacTendermintGroups,
  shouldMergeTendermintGroups,
  shouldMergeTonGroups,
  shouldSkipChainTypeReset,
}: StatisticsDataProviderProps) => {
  const { chainType, setChainType } = useChainType({
    chain,
    shouldSkipChainTypeReset,
  });
  const { chainSubType, setChainSubType } = useChainSubType({ chain });

  const { group, groupedEndpoints, groups, setGroupID } = useGroups({
    chain,
    chainType,
    shouldExpandMainnets,
    shouldExpandTestnets,
    shouldMergeTacTendermintGroups,
    shouldMergeTendermintGroups,
    shouldMergeTonGroups,
    shouldMergeExtensions,
  });

  const { chainCallProtocol, setChainCallProtocol } = useChainCallProtocol({
    chainType,
    group,
  });

  const subchainID = useSubchainID();

  const {
    consensus,
    consensusGroup,
    isConsensusEnabled,
    setIsConsensusEnabled,
  } = useConsensus({
    group,
    subchainID,
  });

  const value = useMemo(
    (): ChainSelectorsContextValue => ({
      chain,
      chainCallProtocol,
      chainSubType,
      chainType,
      consensus,
      consensusGroup,
      group,
      groupedEndpoints,
      groups,
      hasConsensusSwitch,
      isConsensusEnabled,
      setChainCallProtocol,
      setChainSubType,
      setChainType,
      setGroupID,
      setIsConsensusEnabled,
    }),
    [
      chain,
      chainCallProtocol,
      chainSubType,
      chainType,
      consensus,
      consensusGroup,
      group,
      groupedEndpoints,
      groups,
      hasConsensusSwitch,
      isConsensusEnabled,
      setChainCallProtocol,
      setChainSubType,
      setChainType,
      setGroupID,
      setIsConsensusEnabled,
    ],
  );

  return (
    <ChainSelectorsContext.Provider value={value}>
      {children}
    </ChainSelectorsContext.Provider>
  );
};
